import React from "react";
import {
  Pagination,
  PaginationItem,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Stack
} from "@mui/material";
import {
  FirstPage,
  LastPage,
  NavigateBefore,
  NavigateNext
} from "@mui/icons-material";
import { useTheme } from "@mui/system";

const AutobiaPagination = ({
  count = 1,
  page = 1,
  onChange,
  size = "medium",
  variant = "outlined",
  shape = "rounded",
  showFirstLast = true,
  showInfo = true,
  showPageSize = true,
  pageSizeOptions = [5, 10, 25, 50, 100],
  pageSize = 10,
  onPageSizeChange,
  totalItems = 0,
  disabled = false,
  sx = {},
}) => {
  const theme = useTheme();

  const handlePageChange = (event, value) => {
    if (onChange) {
      onChange(event, value);
    }
  };

  const handlePageSizeChange = (event) => {
    if (onPageSizeChange) {
      onPageSizeChange(event.target.value);
    }
  };

  // Calculate display info
  const startItem = Math.min((page - 1) * pageSize + 1, totalItems);
  const endItem = Math.min(page * pageSize, totalItems);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2,
        p: 2,
        backgroundColor: "white",
        borderRadius: "8px",
        border: `1px solid ${theme.palette.gray.g200}`,
        ...sx,
      }}
    >
      {/* Left Side - Info */}
      {showInfo && totalItems > 0 && (
        <Typography variant="body2" color="gray.g600">
          Showing {startItem.toLocaleString()}-{endItem.toLocaleString()} of{" "}
          {totalItems.toLocaleString()} items
        </Typography>
      )}

      {/* Center - Pagination */}
      <Pagination
        count={count}
        page={page}
        onChange={handlePageChange}
        size={size}
        variant={variant}
        shape={shape}
        disabled={disabled}
        renderItem={(item) => (
          <PaginationItem
            {...item}
            sx={{
              "&.Mui-selected": {
                backgroundColor: theme.palette.primary.main,
                color: "white",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
              "&:not(.Mui-selected)": {
                color: theme.palette.gray.g600,
                borderColor: theme.palette.gray.g300,
                "&:hover": {
                  backgroundColor: theme.palette.gray.g50,
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
            components={{
              first: showFirstLast ? FirstPage : undefined,
              last: showFirstLast ? LastPage : undefined,
              previous: NavigateBefore,
              next: NavigateNext,
            }}
          />
        )}
      />

      {/* Right Side - Page Size Selector */}
      {showPageSize && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" color="gray.g600">
            Show:
          </Typography>
          <FormControl size="small">
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              disabled={disabled}
              sx={{
                minWidth: 70,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.gray.g300,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.main,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              {pageSizeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      )}
    </Box>
  );
};

// Simple Pagination (without extra features)
export const AutobiaSimplePagination = ({
  count,
  page,
  onChange,
  size = "medium",
  disabled = false,
  sx = {},
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        p: 2,
        ...sx,
      }}
    >
      <Pagination
        count={count}
        page={page}
        onChange={onChange}
        size={size}
        disabled={disabled}
        renderItem={(item) => (
          <PaginationItem
            {...item}
            sx={{
              "&.Mui-selected": {
                backgroundColor: theme.palette.primary.main,
                color: "white",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
              "&:not(.Mui-selected)": {
                color: theme.palette.gray.g600,
                borderColor: theme.palette.gray.g300,
                "&:hover": {
                  backgroundColor: theme.palette.gray.g50,
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
        )}
      />
    </Box>
  );
};

// Table Pagination (minimal design for tables)
export const AutobiaTablePagination = ({
  count,
  page,
  onChange,
  totalItems,
  pageSize,
  sx = {},
}) => {
  const theme = useTheme();
  
  const startItem = Math.min((page - 1) * pageSize + 1, totalItems);
  const endItem = Math.min(page * pageSize, totalItems);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: `1px solid ${theme.palette.gray.g200}`,
        backgroundColor: theme.palette.gray.g25,
        ...sx,
      }}
    >
      <Typography variant="caption" color="gray.g500">
        {startItem}-{endItem} of {totalItems}
      </Typography>
      
      <Pagination
        count={count}
        page={page}
        onChange={onChange}
        size="small"
        variant="outlined"
        siblingCount={0}
        boundaryCount={1}
        renderItem={(item) => (
          <PaginationItem
            {...item}
            sx={{
              minWidth: "32px",
              height: "32px",
              "&.Mui-selected": {
                backgroundColor: theme.palette.primary.main,
                color: "white",
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export default AutobiaPagination;